import { adminPaths } from 'routes/constants/adminPaths';
import { userPaths } from 'routes/constants/userPaths';
import {
  clearPendingPostLoginRedirect,
  setPendingPostLoginRedirect,
} from 'store/slices/postLoginRedirectSlice';
import { store } from 'store/store';

/** localStorage key for the destination URL to restore after login. */
export const POST_LOGIN_REDIRECT_STORAGE_KEY = 'spence_post_login_redirect';

const AUTH_ENTRY_PATHS = new Set<string>([
  userPaths.signIn,
  userPaths.signInPhone,
  userPaths.signInOtp,
  adminPaths.signIn,
  adminPaths.otpVerification,
]);

function readStorage(): Storage | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  return window.localStorage;
}

/** Best-effort storage: quota / private-mode failures must not break auth redirects. */
function ignoreStorageFailure(): void {
  return;
}

function syncPendingRedirectToRedux(path: string | null): void {
  try {
    if (path) {
      store.dispatch(setPendingPostLoginRedirect(path));
      return;
    }

    store.dispatch(clearPendingPostLoginRedirect());
  } catch {
    ignoreStorageFailure();
  }
}

/**
 * Accepts only same-origin relative app paths (`/users/...` or `/admin/...`).
 * Rejects protocol-relative URLs, absolute URLs, and auth entry screens.
 */
export function sanitizePostLoginRedirectPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') {
    return null;
  }

  const trimmed = raw.trim();
  // Relative app paths only. Absolute / protocol-relative URLs never start with a
  // single `/`, so they are rejected here (no separate scheme check needed).
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return null;
  }

  let pathname = trimmed;
  let search = '';
  let hash = '';

  const hashIndex = pathname.indexOf('#');
  if (hashIndex >= 0) {
    hash = pathname.slice(hashIndex);
    pathname = pathname.slice(0, hashIndex);
  }

  const searchIndex = pathname.indexOf('?');
  if (searchIndex >= 0) {
    search = pathname.slice(searchIndex);
    pathname = pathname.slice(0, searchIndex);
  }

  if (!pathname.startsWith('/users') && !pathname.startsWith('/admin')) {
    return null;
  }

  if (AUTH_ENTRY_PATHS.has(pathname)) {
    return null;
  }

  return `${pathname}${search}${hash}`;
}

export function buildPostLoginRedirectPath(
  pathname: string,
  search = '',
  hash = ''
): string | null {
  const normalizedSearch = search && !search.startsWith('?') ? `?${search}` : search;
  const normalizedHash = hash && !hash.startsWith('#') ? `#${hash}` : hash;
  return sanitizePostLoginRedirectPath(`${pathname}${normalizedSearch}${normalizedHash}`);
}

export function setPostLoginRedirect(path: string): void {
  const sanitized = sanitizePostLoginRedirectPath(path);
  if (!sanitized) {
    return;
  }

  syncPendingRedirectToRedux(sanitized);

  const storage = readStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, sanitized);
  } catch {
    ignoreStorageFailure();
  }
}

/** Persist the current location when auth is required before navigating to sign-in. */
export function capturePostLoginRedirectFromLocation(parts: {
  pathname: string;
  search?: string;
  hash?: string;
}): void {
  const path = buildPostLoginRedirectPath(parts.pathname, parts.search ?? '', parts.hash ?? '');
  if (!path) {
    return;
  }

  setPostLoginRedirect(path);
}

export function capturePostLoginRedirectFromWindow(): void {
  if (typeof window === 'undefined') {
    return;
  }

  capturePostLoginRedirectFromLocation({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  });
}

export function peekPostLoginRedirect(): string | null {
  try {
    const fromRedux = sanitizePostLoginRedirectPath(store.getState().postLoginRedirect?.path);
    if (fromRedux) {
      return fromRedux;
    }
  } catch {
    ignoreStorageFailure();
  }

  const storage = readStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    const sanitized = sanitizePostLoginRedirectPath(raw);
    if (raw && !sanitized) {
      storage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    }

    if (sanitized) {
      syncPendingRedirectToRedux(sanitized);
    }

    return sanitized;
  } catch {
    ignoreStorageFailure();
    return null;
  }
}

export function clearPostLoginRedirect(): void {
  syncPendingRedirectToRedux(null);

  const storage = readStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  } catch {
    ignoreStorageFailure();
  }
}

/**
 * Returns a valid stored redirect and clears it.
 * Invalid / missing values are cleared as well so stale entries cannot loop.
 */
export function consumePostLoginRedirect(): string | null {
  const path = peekPostLoginRedirect();
  clearPostLoginRedirect();
  return path;
}

/** Paths under /users/onboarding or admin org-onboarding (stale resume targets). */
function isStaleOnboardingRedirectPath(path: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0] ?? path;
  return (
    pathname === userPaths.onboarding ||
    pathname.startsWith(`${userPaths.onboarding}/`) ||
    pathname === adminPaths.onboarding ||
    pathname.startsWith(`${adminPaths.onboarding}/`)
  );
}

/**
 * Former employee "home" after login. Full-flow users now land on Expenses;
 * do not restore HSA Paydays after re-auth (session expiry / logout / fresh login).
 */
function isEmployeeHsaPaydaysRedirectPath(path: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0] ?? path;
  return pathname === userPaths.hsaPaydays;
}

/**
 * Prefers a stored email-link destination when the user can leave onboarding.
 * Always clears the stored value so normal logins are not affected later.
 *
 * Never restore a stale onboarding URL when the live journey already left onboarding
 * (e.g. profile_enhanced + connected → Home must not reopen Find Expenses).
 * Never restore HSA Paydays — employee post-login home is Expenses.
 */
export function resolvePostLoginDestinationPath(
  journeyPath: string,
  preferStored: boolean
): string {
  const stored = peekPostLoginRedirect();
  clearPostLoginRedirect();

  if (!preferStored || !stored) {
    return journeyPath;
  }

  if (isEmployeeHsaPaydaysRedirectPath(stored)) {
    return journeyPath;
  }

  if (isStaleOnboardingRedirectPath(stored) && !isStaleOnboardingRedirectPath(journeyPath)) {
    return journeyPath;
  }

  return stored;
}
